import { translations } from "../i18n/translations";
import type { Language } from "../context/LanguageContext";
import axios from "axios";
import type {
  ApplicationFormData,
  BackendParsedData,
} from "../types/application";

// Relative path — Nginx proxies /api/* to the backend and injects the API key server-side.
const API_BASE_URL = "/api";

export interface ResumeUploadResponse {
  success: boolean;
  message: string;
  documentId?: string;
  blob_url?: string;
}

interface ResumeUploadApiResponse extends ResumeUploadResponse {
  parse_id?: string;
}

export interface ResumeParseResponse {
  success: boolean;
  data?: BackendParsedData;
  error?: string;
  confidence_score?: number;
}

export interface MLStatusResponse {
  success: boolean;
  ready: boolean;
  capabilities: {
    basic: boolean;
    advanced: boolean;
    llm: boolean;
  };
  models: {
    layoutlmv3: string;
    llama2: string;
    spacy: string;
  };
  parsing_modes?: {
    [key: string]: string;
  };
}

class ResumeParserService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  /**
   * Check if data is already in frontend format (camelCase)
   */
  /* private isFrontendFormat(data: unknown): boolean {
    // Check for frontend camelCase fields vs backend snake_case
    const typedData = data as Record<string, unknown>;
    return (
      typedData &&
      (typedData.firstName !== undefined || // Frontend has firstName, backend has first_name
        typedData.enhancedData !== undefined || // Frontend has enhancedData, backend has enhanced_data
        typedData.extractionMetadata !== undefined) // Frontend specific field
    );
  } */

  /**
   * Convert backend parsed data to frontend ApplicationFormData format
   * If data is already in frontend format, return as-is
   */
  private convertBackendToFrontend(
    data: BackendParsedData | ApplicationFormData
  ): ApplicationFormData {
    // Check if data is already in frontend format (has camelCase fields)

    //Predefined variables to place deter if its not on list then place to the 'other' field
    // Question 24: Programming Languages - using backend values
    const programmingLanguages = [
      "C#",
      "C++",
      "Go",
      "Java",
      "JavaScript",
      "Python",
      "R",
      "SQL",
      "TypeScript",
      "Other",
    ];

    // Question 25: Frameworks and Libraries - using backend values
    const frameworksLibraries = [
      ".NET",
      "Angular",
      "Node.js",
      "PyTorch",
      "React",
      "Scikit-learn",
      "Spring Boot",
      "TensorFlow",
      "Other",
    ];

    // Question 26: Cloud Operations and Tools
    const cloudOpsTools = [
      "Ansible",
      "AWS",
      "Azure",
      "Cloud Formation",
      "Docker",
      "GCP",
      "Kubernetes",
      "Terraform",
      "Other",
    ];

    // Question 27: Data & Analytics Tools
    const dataAnalyticsTools = [
      "Data Engineering",
      "Data Warehousing",
      "Databricks",
      "ETL",
      "Excel",
      "Looker",
      "Pipelines",
      "Power Query",
      "Power BI",
      "Snowflake",
      "SSIS / SSRS",
      "Tableau",
      "Other",
    ];

    // Question 28: AI/ML, Data Science, and Cloud ML Tools
    const aiMlTools = [
      "AWS SageMaker",
      "Azure Machine Learning",
      "Django",
      "Docker",
      "Flask",
      "Google Cloud AI Platform",
      "Hugging Face Transformers",
      "IBM Watson",
      "Jupyter Notebooks",
      "Keras",
      "Kubernetes",
      "LightGBM",
      "Microsoft Copilot",
      "MLflow",
      "NoSQL (MongoDB, etc.)",
      "PowerBI",
      "PyTorch",
      "Scikit-learn",
      "SQL (PostgreSQL, MySQL, etc.)",
      "Tableau",
      "TensorFlow",
      "XGBoost",
      "Other",
    ];

    // Question 29: Cybersecurity Areas and Tools
    const cybersecurityTools = [
      "1 Password MSP Edition",
      "ArmyG6-CA Cyber Concept",
      "Burp Suite",
      "Canadian Cyber Security Tool (CCST)",
      "Chaîne de Montage",
      "Check Point Quantum",
      "Cloud Security",
      "CNR Virtuel pour Azure (MapleTap)",
      "CrowdStrike Falcon",
      "IAM (Identity & Access Management)",
      "Metasploit",
      "Penetration Testing",
      "Pont d'Observation",
      "SIEM",
      "SOC (Security Operations Center)",
      "Splunk",
      "Target Security Operating Model (TSOM)",
      "Wireshark",
      "Zero Trust Architecture",
      "Other",
    ];

    // Question 30: DevOps Tools
    const devOpsTools = [
      "Argo CD",
      "Azure DevOps",
      "Bamboo",
      "CircleCI",
      "Docker",
      "GitHub Actions",
      "GitLab CI/CD",
      "Jenkins",
      "Nexus Repository",
      "OpenShift",
      "Pulumi",
      "SonarQube",
      "Spinnaker",
      "Terraform",
      "Travis CI",
      "Other",
    ];

    // Question 31: Version Control Tools
    const versionControlTools = [
      "Azure Repos",
      "Bitbucket",
      "Git",
      "GitHub",
      "GitLab",
      "Mercurial",
      "Perforce (Helix Core)",
      "Rational ClearCase",
      "Other",
    ];

    // Question 32: Enterprise Tools & Platforms
    const enterpriseTools = [
      "Informatica",
      "M365 (Microsoft 365)",
      "Oracle",
      "Peoplesoft",
      "Salesforce",
      "SAP",
      "ServiceNow",
      "SharePoint",
      "Teams",
      "Other",
    ];

    // Question 33: Project & Collaboration Tools
    const projectCollabTools = [
      "Azure DevOps",
      "Confluence",
      "D365 (Dynamics 365)",
      "GCdocs",
      "GCpedia / GCconnex / GCcollab",
      "GitHub Projects",
      "Jira",
      "Microsoft Teams",
      "MS Project",
      "SharePoint",
      "Trello",
      "Other",
    ];

    // Question 34: Project Management Methodologies
    const projectMgmtMethodologies = [
      "Agile-Waterfall Hybrid",
      "Critical Path Method (CPM)",
      "Crystal",
      "Disciplined Agile Delivery (DAD)",
      "Dynamic Systems Development Method (DSDM)",
      "Extreme Programming (XP)",
      "Integrated Project Delivery (IPD)",
      "Kanban",
      "Lean",
      "Lean Six Sigma",
      "LeSS (Large Scale Scrum)",
      "Nexus",
      "PMBOK (Project Management Body of Knowledge)",
      "PRINCE2",
      "PRINCE2 Agile",
      "Program Evaluation and Review Technique (PERT)",
      "SAFe (Scaled Agile Framework)",
      "Scrum",
      "SixSigma",
      "Spotify Model",
      "Waterfall",
      "Other",
    ];

    // Cast to backend format for conversion
    const backendData = data as BackendParsedData;
    console.log("check", backendData);
    // Track which fields were extracted
    const extractedFields: string[] = [];
    const missingFields: string[] = [];

    // Helper function to track field extraction with proper typing
    const trackField = <T>(fieldName: string, value: T, defaultValue: T): T => {
      if (
        value &&
        value !== "" &&
        value !== 0 &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        extractedFields.push(fieldName);
        return value;
      } else {
        missingFields.push(fieldName);
        return defaultValue;
      }
    };

    const splitExtractedList = (value: unknown): string[] => {
      const values = Array.isArray(value) ? value : value ? [value] : [];

      return values
        .flatMap((item) => String(item).split(/[,;|]/))
        .map((item) => item.trim())
        .filter(Boolean);
    };

    // Convert enhanced data if available
    let enhancedData = undefined;
    if (backendData.enhancedData) {
      const enhanced = backendData.enhancedData;
      enhancedData = {
        citizenship: trackField("citizenship", enhanced.citizenship || "", ""),
        currentLocation: trackField(
          "currentLocation",
          enhanced.currentLocation || "",
          ""
        ),
        priorityEntitlementStatus: trackField(
          "priorityEntitlementStatus",
          enhanced.priorityEntitlementStatus || "",
          ""
        ),
        currentlyEmployedFPS: trackField(
          "currentlyEmployedFPS",
          enhanced.currentlyEmployedFps || "",
          ""
        ),
        currentLevel: trackField(
          "currentLevel",
          enhanced.currentLevel || "",
          ""
        ),
        currentClassification: trackField(
          "currentClassification",
          enhanced.currentClassification || "",
          ""
        ),
        typeOfEmployment: trackField(
          "typeOfEmployment",
          enhanced.typeOfEmployment || "",
          ""
        ),
        currentDepartment: trackField(
          "currentDepartment",
          enhanced.currentDepartment || "",
          ""
        ),
        employmentEndDate: trackField(
          "employmentEndDate",
          enhanced.employmentEndDate || "",
          ""
        ),
        priNumber: trackField("priNumber", enhanced.priNumber || "", ""),
        firstOfficialLanguage: trackField(
          "firstOfficialLanguage",
          enhanced.firstOfficialLanguage || "",
          ""
        ),
        validSLEResults: trackField(
          "validSLEResults",
          splitExtractedList(enhanced.validSleResults),
          []
        ),
        validSecurityClearance: trackField(
          "validSecurityClearance",
          enhanced.validSecurityClearance || "",
          ""
        ),
        highestEducationLevel: trackField(
          "highestEducationLevel",
          enhanced.highestEducationLevel || "",
          ""
        ),
        postSecondaryEducationLevel: trackField(
          "postSecondaryEducationLevel",
          enhanced.postSecondaryEducationLevel || "",
          ""
        ),
        postSecondaryInstitution: trackField(
          "postSecondaryInstitution",
          enhanced.postSecondaryInstitution || "",
          ""
        ),
        areaOfStudy: trackField("areaOfStudy", enhanced.areaOfStudy || "", ""),
        graduationDate: trackField(
          "graduationDate",
          enhanced.graduationDate || "",
          ""
        ),
        lookingForITRole: trackField(
          "lookingForITRole",
          enhanced.lookingForItRole || "",
          ""
        ),
        currentLanguageProfile: trackField(
          "currentLanguageProfile",
          enhanced.currentLanguageProfile || "",
          ""
        ),
        sleExpiryDate: trackField(
          "sleExpiryDate",
          enhanced.sleExpiryDate || "",
          ""
        ),
        languageProfilesInterested: trackField(
          "languageProfilesInterested",
          splitExtractedList(enhanced.languageProfilesInterested),
          []
        ),
        // Map IT competencies to flat structure in enhancedData
        programmingLanguages: enhanced
          ? trackField(
              "programmingLanguages",
              enhanced.programmingLanguages.filter((v: string) =>
                programmingLanguages.includes(v)
              ) || [],
              []
            )
          : [],
        programmingLanguages_other: enhanced
          ? trackField(
              "programmingLanguages_other",
              (
                enhanced.programmingLanguages.filter(
                  (v: string) => !programmingLanguages.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",
        frameworks: enhanced
          ? trackField(
              "frameworks",
              enhanced.frameworksLibraries.filter((v: string) =>
                frameworksLibraries.includes(v)
              ) || [],
              []
            )
          : [],
        frameworks_other: enhanced
          ? trackField(
              "frameworks_other",
              (
                enhanced.frameworksLibraries.filter(
                  (v: string) => !frameworksLibraries.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        cloudOpsTools: enhanced
          ? trackField(
              "cloudOpsTools",
              enhanced.cloudOpsTools.filter((v: string) =>
                cloudOpsTools.includes(v)
              ) || [],
              []
            )
          : [],

        cloudOpsTools_other: enhanced
          ? trackField(
              "cloudOpsTools_other",
              (
                enhanced.cloudOpsTools.filter(
                  (v: string) => !cloudOpsTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        dataAnalyticsTools: enhanced
          ? trackField(
              "dataAnalyticsTools",
              enhanced.dataAnalyticsTools.filter((v: string) =>
                dataAnalyticsTools.includes(v)
              ) || [],
              []
            )
          : [],

        dataAnalyticsTools_other: enhanced
          ? trackField(
              "dataAnalyticsTools_other",
              (
                enhanced.dataAnalyticsTools.filter(
                  (v: string) => !dataAnalyticsTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        aiMlTools: enhanced
          ? trackField(
              "aiMlTools",
              enhanced.aiMlTools.filter((v: string) => aiMlTools.includes(v)) ||
                [],
              []
            )
          : [],

        aiMlTools_other: enhanced
          ? trackField(
              "aiMlTools_other",
              (
                enhanced.aiMlTools.filter(
                  (v: string) => !aiMlTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        cybersecurityTools: enhanced
          ? trackField(
              "cybersecurityTools",
              enhanced.cybersecurityTools.filter((v: string) =>
                cybersecurityTools.includes(v)
              ) || [],
              []
            )
          : [],

        cybersecurityTools_other: enhanced
          ? trackField(
              "cybersecurityTools_other",
              (
                enhanced.cybersecurityTools.filter(
                  (v: string) => !cybersecurityTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        devOpsTools: enhanced
          ? trackField(
              "devOpsTools",
              enhanced.devopsTools.filter((v: string) =>
                devOpsTools.includes(v)
              ) || [],
              []
            )
          : [],
        devOpsTools_other: enhanced
          ? trackField(
              "devOpsTools_other",
              (
                enhanced.devopsTools.filter(
                  (v: string) => !devOpsTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",
        versionControlTools: enhanced
          ? trackField(
              "versionControlTools",
              enhanced.versionControlTools.filter((v: string) =>
                versionControlTools.includes(v)
              ) || [],
              []
            )
          : [],

        versionControlTools_other: enhanced
          ? trackField(
              "versionControlTools_other",
              (
                enhanced.versionControlTools.filter(
                  (v: string) => !versionControlTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",

        enterpriseTools: enhanced
          ? trackField(
              "enterpriseTools",
              enhanced.enterpriseTools.filter((v: string) =>
                enterpriseTools.includes(v)
              ) || [],
              []
            )
          : [],

        enterpriseTools_other: enhanced
          ? trackField(
              "enterpriseTools_other",
              (
                enhanced.enterpriseTools.filter(
                  (v: string) => !enterpriseTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",
        projectMgmtMethodologies: enhanced
          ? trackField(
              "projectMgmtMethodologies",
              enhanced.projectManagementTools.filter((v: string) =>
                projectMgmtMethodologies.includes(v)
              ) || [],
              []
            )
          : [],

        projectMgmtMethodologies_other: enhanced
          ? trackField(
              "projectMgmtMethodologies_other",
              (
                enhanced.projectManagementTools.filter(
                  (v: string) => !projectMgmtMethodologies.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",
        projectCollabTools_other: enhanced
          ? trackField(
              "projectCollabTools_other",
              (
                enhanced.collaborationTools.filter(
                  (v: string) => !projectCollabTools.includes(v)
                ) || []
              ).join(", "),
              ""
            )
          : "",
        projectCollabTools: enhanced
          ? trackField(
              "projectCollabTools",
              enhanced.collaborationTools.filter((v: string) =>
                projectCollabTools.includes(v)
              ) || [],
              []
            )
          : [],
      };
    }
    return {
      firstName: trackField("firstName", backendData.firstName || "", ""),
      lastName: trackField("lastName", backendData.lastName || "", ""),
      email: trackField("email", backendData.email || "", ""),
      phone: trackField("phone", backendData.phone || "", ""),
      linkedInUrl: trackField(
        "linkedInUrl",
        backendData.linkedinUrl || "",
        undefined
      ),
      AIConsentStatus: trackField("AIConsentStatus", "", ""),
      currentTitle: trackField(
        "currentTitle",
        backendData.currentTitle || "",
        ""
      ),
      currentCompany: trackField(
        "currentCompany",
        backendData.currentCompany || "",
        ""
      ),
      yearsOfExperience: trackField(
        "yearsOfExperience",
        backendData.yearsOfExperience || 0,
        0
      ),
      technicalSkills: trackField(
        "technicalSkills",
        backendData.technicalSkills || [],
        []
      ),
      softSkills: trackField("softSkills", backendData.softSkills || [], []),
      certifications: trackField(
        "certifications",
        backendData.certifications || [],
        []
      ),
      experiences:
        backendData.experiences?.map((exp) => ({
          company: exp.company || "",
          title: exp.title || "",
          location: exp.location || "",
          startDate: exp.start_date || "",
          endDate: exp.end_date || "",
          isCurrent: exp.is_current || false,
          description: exp.description || "",
          achievements: exp.achievements || [],
        })) || [],
      education:
        backendData.education?.map((edu) => ({
          institution: edu.institution || "",
          degree: edu.degree || "",
          field: edu.field || "",
          location: edu.location || "",
          startDate: edu.start_date || "",
          endDate: edu.end_date || "",
          gpa: edu.gpa || "",
          description: edu.description || "",
        })) || [],
      coverLetter: trackField("coverLetter", backendData.coverLetter || "", ""),
      portfolioUrl: trackField(
        "portfolioUrl",
        backendData.portfolioUrl || "",
        undefined
      ),
      availabilityDate: trackField(
        "availabilityDate",
        backendData.availabilityDate || "",
        ""
      ),
      salaryExpectation: trackField(
        "salaryExpectation",
        backendData.salaryExpectation || "",
        undefined
      ),
      workLocation:
        (backendData.workLocation as "onsite" | "remote" | "hybrid") ||
        "remote",
      positionTitle: trackField(
        "positionTitle",
        backendData.positionTitle || "",
        ""
      ),
      enhancedData,
      extractionMetadata: {
        wasExtracted: true,
        extractedFields,
        missingFields,
      },
    };
  }

  /**
   * Parse resume using upload and wait method (uses existing backend endpoints).
   *
   * @param file Resume file (PDF or DOCX).
   * @returns Parsed resume data.
   */
  async parseResumeDirectly(
      file: File,
      language: Language = 'en'
    ): Promise<ResumeParseResponse> {
    try {
      // Use the existing upload endpoint
      const uploadResponse = await this.uploadResume(file, "Yes", language);

      if (!uploadResponse.success || !uploadResponse.documentId) {
        throw new Error(uploadResponse.message || translations[language].resumeUploadError);
      }

      // Wait for processing to complete using the existing endpoint
      const parseResponse = await this.waitForParsedData(
        language,
        uploadResponse.documentId
      );

      return parseResponse;
    } catch (error) {
      console.error("Error parsing resume directly:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          throw new Error(
            translations[language].AINotReady
          );
        } else if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        } else if (error.response?.status === 413) {
          throw new Error(
            translations[language].fileTooLarge
          );
        } else if (
          error.response?.status &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          throw new Error(
            translations[language].resumeFileError
          );
        }
      }

      throw new Error(translations[language].resumeParseError);
    }
  }

  /**
   * Get ML model status and capabilities (NEW)
   * @returns ML service status
   */
  async getMLStatus(language: Language = 'en'): Promise<MLStatusResponse> {
    try {
      const response = await axios.get<MLStatusResponse>(
        `/api/ml/status`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting ML status:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        }
      }

      throw new Error(translations[language].failedMLStatus);
    }
  }

  /**
   * Check if offline ML models are ready
   * @returns True if ML models are ready for direct parsing
   */
  async isMLReady(language: Language = 'en'): Promise<boolean> {
    try {
      const status = await this.getMLStatus(language);
      return status.ready;
    } catch (error) {
      console.warn("Could not check ML status:", error);
      return false;
    }
  }
  /**
   * Upload and parse a resume file with background processing.
   *
   * @param file Resume file (PDF or DOCX).
   * @returns Upload response with `parse_id`.
   */
  async uploadResume(
    file: File,
    AIConsentStatus: string,
    language: Language = 'en'
  ): Promise<ResumeUploadResponse> {
    const validation = this.validateFile(file, language);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      const response =
        AIConsentStatus === "Yes"
          ? // User consented → use normal upload (with background parsing)
            await this.api.post<ResumeUploadApiResponse>("/resume/upload", {
              file,
            })
          : // User did NOT consent → use upload-only endpoint
            await this.api.post<ResumeUploadApiResponse>("/resume/upload-only", {
              file,
              AIConsentStatus,
            });

      console.log("Upload response:", response.data);

      // Handle different response formats
      if (response.data) {
        // Check if we have a documentId or parse_id
        const document_id = response.data.documentId || response.data.parse_id;

        // Handle success cases
        if (response.data.success || response.status === 200) {
          return {
            success: true,
            message: response.data.message || "File uploaded successfully",
            documentId: document_id,
            blob_url: response.data.blob_url,
          };
        } else {
          return response.data;
        }
      }

      throw new Error(translations[language].invalidResponse);
    } catch (error) {
      console.error("Error uploading resume:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        } else if (error.response?.status === 413) {
          throw new Error(
            translations[language].fileTooLarge
          );
        } else if (
          error.response?.status &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          throw new Error(
            translations[language].resumeFileError
          );
        }
      }

      throw new Error(translations[language].resumeUploadError);
    }
  }

  /**
   * Get parsed resume data by parse_id
   * @param parseId Unique ID for the parsing operation
   * @returns Parsed resume data
   */
  async getParsedData(
    parseId: string,
    language: Language = 'en'
  ): Promise<ResumeParseResponse> {
    try {
      const response = await axios.get(
        `/api/v1/resumes/${parseId}/parse`
      );

      console.log("Backend response:", response.data);

      // Check if the response has the expected structure
      if (response.data && response.data.extracted_data) {
        // Backend returns extracted_data directly
        return {
          success: true,
          data: response.data.extracted_data,
          confidence_score: response.data.confidence_scores?.overall,
        };
      } else if (
        response.data &&
        (response.data.message || response.data.detail)
      ) {
        // Handle status messages - backend can return either 'message' or 'detail'
        const statusMessage =
          response.data.message || response.data.detail || "";

        if (
          statusMessage.includes("still being processed") ||
          statusMessage.includes("Processing started") ||
          response.data.processing_status === "processing"
        ) {
          return {
            success: false,
            error: "still being processed",
          };
        } else {
          return {
            success: false,
            error: statusMessage || "Unknown processing error",
          };
        }
      } else {
        return {
          success: false,
          error: "Unexpected response format",
        };
      }
    } catch (error) {
      console.error("Error getting parsed data:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 202) {
          // Document is still being processed (HTTP 202)
          return {
            success: false,
            error: "still being processed",
          };
        } else if (error.response?.status === 404) {
          throw new Error(
            translations[language].parseDataNotFound
          );
        } else if (error.response?.data?.detail) {
          // Handle detail messages from FastAPI
          if (error.response.data.detail.includes("still being processed")) {
            return {
              success: false,
              error: "still being processed",
            };
          } else {
            throw new Error(error.response.data.detail);
          }
        } else if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
      }

      throw new Error(translations[language].parseDataNotRetrieved);
    }
  }

  /**
   * Get missing mandatory fields for a document
   * @param parseId Document ID
   * @returns Missing fields information
   */
  async getMissingMandatoryFields(parseId: string, language: Language = 'en'): Promise<{
    document_id: string;
    missing_mandatory_fields: string[];
    requires_user_input: boolean;
    total_missing: number;
    message: string;
  }> {
    try {
      const response = await axios.get(
        `/api/v1/resumes/${parseId}/missing-fields`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting missing fields:", error);
      throw new Error(translations[language].failedMissingInfoFeilds);
    }
  }

  /**
   * Complete missing mandatory fields
   * @param parseId Document ID
   * @param fieldData Field values to complete
   * @returns Completion result
   */
  async completeMandatoryFields(
    parseId: string,
    fieldData: Record<string, string>,
    language: Language = 'en'
  ): Promise<{
    document_id: string;
    message: string;
    updated_fields: string[];
    requires_user_input: boolean;
  }> {
    try {
      const response = await axios.post(
        `/api/v1/resumes/${parseId}/complete-fields`,
        fieldData
      );
      return response.data;
    } catch (error) {
      console.error("Error completing fields:", error);
      throw new Error(translations[language].failedToCompleteFeilds);
    }
  }

  /**
   * Poll for parsed data with retries
   * @param parseId Unique ID for the parsing operation
   * @param maxAttempts Maximum number of polling attempts (default: 3)
   * @param intervalMs Polling interval in milliseconds (default: 2000)
   * @returns Parsed resume data when ready
   */
  async waitForParsedData(
    language: Language = 'en',
    parseId: string,
    maxAttempts: number = 3,
    intervalMs: number = 2000,
  ): Promise<ResumeParseResponse> {
    console.log(`Starting to poll for parsed data. Document ID: ${parseId}`);

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`Polling attempt ${attempt}/${maxAttempts}`);
        const result = await this.getParsedData(parseId, language);

        if (result.success && result.data) {
          console.log("Parsing completed successfully");
          return result;
        } else if (
          result.error &&
          result.error.includes("still being processed")
        ) {
          console.log("Still processing, waiting...");
          // If still processing, wait and retry
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
          }
        } else if (result.error) {
          // If there's a real error, throw it
          console.error("Parsing error:", result.error);
          throw new Error(result.error);
        }
      } catch (error) {
        console.error(`Polling attempt ${attempt} failed:`, error);

        if (attempt === maxAttempts) {
          throw error;
        }
        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error(translations[language].resumeParseTimeOut);
  }

  /**
   * Smart resume parsing - automatically chooses best method
   * @param file Resume file
   * @param preferDirect Prefer direct parsing if available (default: true)
   * @returns Parsed resume data
   */
  async parseResumeSmartly(
    file: File,
    AIConsentStatus: string,
    language: Language = 'en'
  ): Promise<ApplicationFormData> {
    // Always use the upload and wait method since that's what works
    console.log("Using upload and wait method...");
    return await this.uploadAndParseResume(file, AIConsentStatus, language);
  }

  /**
   * Upload and wait for parsed data in one call (background processing)
   * @param file Resume file
   * @returns Parsed resume data
   */
  async uploadAndParseResume(
    file: File,
    AIConsentStatus: string,
    language: Language = 'en'
  ): Promise<ApplicationFormData> {
    console.log("Starting upload and parse process...");

    // Upload the file
    const uploadResponse = await this.uploadResume(file, AIConsentStatus, language);
    console.log("Upload response:", uploadResponse);

    if (!uploadResponse.success || !uploadResponse.documentId) {
      throw new Error(uploadResponse.message || translations[language].resumeUploadError);
    }

    //Confirm if consented or not
    if (AIConsentStatus === "Yes") {
      console.log("Upload successful, waiting for parsing...");

      // Wait for parsing to complete with extended timeout for AI processing
      const parseResponse = await this.waitForParsedData(
        language,
        uploadResponse.documentId,
        10, // Increase to 10 attempts
        8000 // Increase to 8 seconds between attempts (total: 80 seconds)
      );

      if (!parseResponse.success || !parseResponse.data) {
        throw new Error(parseResponse.error || "Failed to parse resume");
      }

      console.log("Parsing successful, converting data...");
      const convertedData = this.convertBackendToFrontend(parseResponse.data);

      // Add the document ID to the converted data
      convertedData.documentId = uploadResponse.documentId;

      console.log("Converted data:", convertedData);

      return convertedData;
    } else {
      console.log("User did not consent to AI. Returning upload info only.");

      return {
        // Personal Info
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        linkedInUrl: "",

        AIConsentStatus, // store user's choice

        // Professional Info
        currentTitle: "",
        currentCompany: "",
        yearsOfExperience: 0,

        // Skills
        technicalSkills: [],
        softSkills: [],
        certifications: [],

        // Experience & Education
        experiences: [],
        education: [],

        // Additional Info
        coverLetter: "",
        portfolioUrl: "",
        availabilityDate: "",
        salaryExpectation: "",
        workLocation: "onsite", // default safe value
        positionTitle: "",

        // Resume
        resumeFile: file,
        resumeUrl: uploadResponse.blob_url,
        documentId: uploadResponse.documentId,

        // Enhanced Gov App Data
        enhancedData: {},

        // Extraction Metadata
        extractionMetadata: {
          wasExtracted: false,
          extractedFields: [],
          missingFields: [],
        },
      };
    }
  }

  /**
   * Parse resume with progress callback
   * @param file Resume file
   * @param onProgress Progress callback function
   * @param preferDirect Prefer direct parsing if available
   * @returns Parsed resume data
   */
  async parseResumeWithProgress(
    file: File,
    language: Language = 'en',
    onProgress?: (stage: string, progress: number) => void
  ): Promise<ApplicationFormData> {
    onProgress?.("Initializing...", 0);

    try {
      // Use the working upload and wait method
      onProgress?.("Uploading resume...", 20);
      const uploadResponse = await this.uploadResume(file, "Yes", language);

      if (!uploadResponse.success || !uploadResponse.documentId) {
        throw new Error(uploadResponse.message || translations[language].resumeUploadError);
      }

      onProgress?.("Processing document...", 40);

      // Poll with progress updates
      const maxAttempts = 30;
      const intervalMs = 2000;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const result = await this.getParsedData(uploadResponse.documentId, language);

          if (result.success && result.data) {
            onProgress?.("Parsing completed!", 100);
            return this.convertBackendToFrontend(result.data);
          } else if (
            result.error &&
            !result.error.includes("still being processed")
          ) {
            throw new Error(result.error);
          }

          // Update progress
          const progress = 40 + Math.min((attempt / maxAttempts) * 50, 50);
          onProgress?.("Processing document...", Math.round(progress));

          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
          }
        } catch (error) {
          if (attempt === maxAttempts) {
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }

      throw new Error(translations[language].resumeParseTimeOut);
    } catch (error) {
      console.error("Resume processing failed:", error);
      throw error;
    }
  }

  /**
   * Validate file before upload
   * @param file Resume file to validate
   * @param language Language for the validation error message
   * @returns Validation result
   */
  validateFile(
    file: File,
    language: Language = 'en'
  ): { valid: boolean; error?: string } {
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    const supportedMimeTypes: Record<string, string> = {
      ".pdf": "application/pdf",
      ".docx":
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };

    if (!file) {
      return { valid: false, error: "No file selected" };
    }

    if (file.size > maxSizeBytes) {
      return { valid: false, error: "File size exceeds 10MB limit" };
    }

    const fileName = file.name.trim().toLowerCase();
    const fileExtension = fileName.slice(fileName.lastIndexOf("."));
    const expectedMimeType = supportedMimeTypes[fileExtension];
    const reportedMimeType = file.type.toLowerCase();

    if (
      !expectedMimeType ||
      (reportedMimeType && reportedMimeType !== expectedMimeType)
    ) {
      return {
        valid: false,
        error: translations[language].filesErrorMessage,
      };
    }

    return { valid: true };
  }
}

export const resumeParserService = new ResumeParserService();
